import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isWidgetDisplayed } from "../../esri/widgets/expandUtils";
import useModuleSelection from "../../hooks/useModuleSelection";
import { closeSidebar } from "../../redux/action/Sidebar-actions";
import {
  getOperationPreferences,
  getOpsColor,
  ROLE_EDITOR,
  triggerGAEvent,
} from "../../utils/helper";
import { MOBILE_SCREENS } from "../App/App";
import LanguageDropdown from "../Header/components/LanguageDropdown/LanguageDropdown";
import { ArrowDownIcon, CloseIcon } from "../Icons";
import MapDisclaimer from "../MapDisclaimer/MapDisclaimer";
import { Button, IconButton, Modal } from "../UI";
import ModuleBox from "./ModuleBox";
import {
  StyledArrowIconWrapper,
  StyledDisclaimerContainer,
  StyledLanguageContainer,
  StyledModalBody,
  StyledModalContent,
  StyledModalFooter,
  StyledModalFooterContainer,
  StyledModalTitle,
  StyledModuleWrapper,
} from "./Modules-styled";

const Modules = ({
  show,
  setShow,
  config,
  setMobileScreen,
  isHeaderShown,
  onBoardingTour,
  children,
}) => {
  const [savePreferences, setSavePreferences] = useState(false);
  const [expandedModule, setExpandedModule] = useState(null);
  const [bookmarkedModule, setBookmarkedModule] = useState(null);
  const [showArrow, setShowArrow] = useState(false);
  const { t, i18n } = useTranslation("common");
  const { modules, handleModuleSelection, activeModule } =
    useModuleSelection(config);
  const ref = useRef();
  const scrollableRef = useRef();
  const dispatch = useDispatch();

  const isModuleSelected = useRef(false);

  useEffect(() => {
    if (!isWidgetDisplayed(config, "Onboarding")) return;
    const urlParams = new URLSearchParams(window.location.search);
    const widgetOpen = urlParams.get("widget-open");
    if (widgetOpen === "onboarding" && onBoardingTour) {
      setTimeout(() => {
        handleStartTour();
      }, 1000);
    }
  }, [onBoardingTour]);

  const handleModuleExpand = useCallback(
    (e, module) => {
      if (expandedModule === module) {
        setExpandedModule(null);
      } else {
        setExpandedModule(module);
      }
      setExpandedModule(module);
    },
    [expandedModule]
  );

  const handleHide = useCallback(
    (module = null) => {
      setShow(false);
      setExpandedModule(module ?? activeModule);
    },
    [activeModule, savePreferences]
  );

  const handleStartTour = useCallback(() => {
    if (onBoardingTour) {
      onBoardingTour.start();
      handleHide();
      dispatch(closeSidebar());

      onBoardingTour.on("complete", () => {
        setShow(true);
      });
    }
  }, [onBoardingTour, handleHide]);

  const onModuleSelect = useCallback(
    (module) => {
      setExpandedModule(module);
      const moduleOpenWidgets = config.modules[activeModule]
        ? config.modules[activeModule].openWidgets || []
        : [];
      const openWidget =
        moduleOpenWidgets.length > 0 ? moduleOpenWidgets[0] : null;
      let activeScreen = MOBILE_SCREENS.MAP;

      if (openWidget && isWidgetDisplayed(config, openWidget)) {
        activeScreen = openWidget;
      }

      if (setMobileScreen) {
        setMobileScreen(activeScreen);
      }
      handleModuleSelection(module);
      isModuleSelected.current = true;
      sendAnalyticsModuleEvent(module);
    },
    [handleHide, handleModuleSelection, config]
  );

  const handleModuleBookmark = useCallback(
    (e, moduleName) => {
      e.stopPropagation();
      const module = moduleName === bookmarkedModule ? null : moduleName;

      const { allPreferences: preferences, operationPreferences } =
        getOperationPreferences(config.id);
      if (module) {
        preferences[config.id] = {
          ...operationPreferences,
          activeModule: module,
        };
      } else {
        const { activeModule, ...rest } = operationPreferences;
        preferences[config.id] = rest;
      }

      if (module) {
        onModuleSelect(module);
      }

      localStorage.setItem("savedPreferences", JSON.stringify(preferences));
      setBookmarkedModule(module);
    },
    [bookmarkedModule, onModuleSelect]
  );

  // useLayoutEffect(() => {
  //   if (!window.isSmall) return;
  //   if (!scrollableRef.current || !show) return;
  //   const el = scrollableRef.current;

  //   setTimeout(() => {
  //     if (el.offsetHeight < el.scrollHeight) {
  //       setShowArrow(true);
  //     }
  //   }, 250);
  // }, [show]);

  const handleScrollDown = useCallback((e) => {
    e.stopPropagation();
    if (!scrollableRef.current) return;
    scrollableRef.current.scrollTo({
      top: scrollableRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  const onScroll = useCallback(
    (e) => {
      // const el = e.target;
      // if (el.offsetHeight + el.scrollTop < el.scrollHeight) {
      //   if (showArrow) return;
      //   setShowArrow(true);
      // } else {
      //   setShowArrow(false);
      // }
    },
    [showArrow]
  );

  const selectedModule = useMemo(() => {
    const { allPreferences: preferences, operationPreferences } =
      getOperationPreferences(config.id);
    return bookmarkedModule || operationPreferences.activeModule;
  }, [bookmarkedModule, config]);

  const opsColor = useMemo(
    () => getOpsColor(config, activeModule),
    [config, activeModule]
  );

  const sendAnalyticsModuleEvent = useCallback((selectedModule) => {
    if (typeof selectedModule === "string") {
      triggerGAEvent({
        event: "module",
        params: {
          module_name: selectedModule.toUpperCase(),
        },
      });
    }
  }, []);

  const hideLanguageSelection = useMemo(() => {
    return (
      !isWidgetDisplayed(config, "LanguageSwitcher") ||
      !(config.supportedLngs && config.supportedLngs.length > 1)
    );
  }, [config]);

  return (
    <Modal show={show} hasContent={false}>
      <div></div>
      {children}
      <StyledModalContent
        className="wrapper"
        show={show}
        ref={ref}
        isHeaderShown={isHeaderShown}
      >
        <span className="arrow"></span>
        {show && (
          <StyledModalBody ref={scrollableRef} onScroll={onScroll}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                position: "relative",
                height: "33px",
              }}
            >
              <StyledModalTitle>
                {t("screen.widget.ModuleSwitcher.choose")}
              </StyledModalTitle>
              <IconButton
                style={{ position: "absolute", right: 0 }}
                onClick={handleHide}
              >
                <CloseIcon />
              </IconButton>
            </div>

            <StyledModuleWrapper>
              {modules.map((module) => (
                <ModuleBox
                  key={module.module}
                  module={module}
                  selectedModule={selectedModule}
                  handleModuleClick={() => {
                    handleHide(module);
                    onModuleSelect(module.module);
                  }}
                  handleBookmarkClick={(e) =>
                    handleModuleBookmark(e, module.module)
                  }
                  t={t}
                />
              ))}
            </StyledModuleWrapper>

            {(config.role !== ROLE_EDITOR || !hideLanguageSelection) && (
              <StyledModalFooter>
                {config.role !== ROLE_EDITOR && (
                  <StyledModalFooterContainer flex={"3"}>
                    <span>
                      {t("screen.widget.Support.onboarding.moduleTitle")}
                    </span>
                    <div style={{ display: "flex", gap: "14px" }}>
                      <Button
                        isNew
                        className="gtag"
                        data-tag-name="Onboarding"
                        id="onboarding-widget--modules"
                        onClick={handleStartTour}
                        bg="rgba(0, 0, 0, 0.08)"
                      >
                        {t("screen.widget.Support.onboarding.title")}
                      </Button>
                    </div>
                  </StyledModalFooterContainer>
                )}

                {!hideLanguageSelection && (
                  <StyledLanguageContainer>
                    <span>
                      {t("screen.widget.LanguageSwitcher.moduleTitle")}
                    </span>
                    <LanguageDropdown
                      id="language-widget--modules"
                      config={config}
                      isModules
                    />
                  </StyledLanguageContainer>
                )}
              </StyledModalFooter>
            )}

            <StyledDisclaimerContainer>
              <MapDisclaimer config={config} isModalBox />
            </StyledDisclaimerContainer>
          </StyledModalBody>
        )}
        <StyledArrowIconWrapper onClick={handleScrollDown} show={showArrow}>
          <ArrowDownIcon />
        </StyledArrowIconWrapper>
      </StyledModalContent>
    </Modal>
  );
};
export default Modules;
